/**
 * timeGridLayout — where every event goes on the Week/Day time grid (#679).
 *
 * Three jobs the old views did not do at all:
 *   1. split all-day (and ≥24h) events out into the band above the hours,
 *      instead of placing them at midnight;
 *   2. clip timed events to each day they touch in the display zone, so an
 *      event crossing midnight draws on both days;
 *   3. lay overlapping events side by side, so two 10:00 meetings are two
 *      visible, separately clickable blocks rather than one on top of the other.
 *
 * Pure functions, no React. Overlap layout is O(n log n) per day column on the
 * events in view (sort, then one sweep) — never pairwise across a fetched range.
 */
import { parseInstant } from '../timezone';
import {
    MINUTES_PER_DAY, allDayRange, coversDay, dayKeyOf, daysBetween, isAllDayLike, minutesOf,
} from './zonedCalendar';

/** Pixels per hour on the grid (prototype `HPX`). */
export const HOUR_PX = 56;
/** The smallest block drawn, so a 5-minute meeting is still clickable (prototype). */
export const MIN_BLOCK_PX = 24;
/** Blocks shorter than this inline their time and drop the meta line (prototype). */
export const SHORT_BLOCK_PX = 40;

/**
 * Split events into the all-day band and per-day timed segments.
 *
 * @param {object[]} events
 * @param {string[]} dayKeys - the columns in view
 * @param {string} zone
 * @returns {{ band: object[], timed: Record<string, object[]> }}
 *   `timed[dayKey]` is a list of segments `{ event, startMin, endMin, continued, continues }`.
 */
export function splitAllDay(events, dayKeys, zone) {
    const band = [];
    const timed = Object.fromEntries(dayKeys.map((k) => [k, []]));

    for (const event of events || []) {
        if (isAllDayLike(event)) {
            const range = allDayRange(event, zone);
            if (range && dayKeys.some((k) => coversDay(range, k))) band.push({ event, range });
            continue;
        }
        const s = parseInstant(event.start);
        const e = parseInstant(event.end);
        if (!s) continue;
        const startKey = dayKeyOf(s, zone);
        // Zero-length or missing end: draw the minimum block at the start.
        const endMs = e && e > s ? e.getTime() : s.getTime();
        const endKey = dayKeyOf(new Date(Math.max(endMs - 1, s.getTime())), zone);
        const startMin = minutesOf(s, zone);
        const endMinOnLastDay = endMs > s.getTime() ? minutesOf(new Date(endMs), zone) || MINUTES_PER_DAY : startMin;

        for (const key of dayKeys) {
            if (key < startKey || key > endKey) continue;
            const isFirst = key === startKey;
            const isLast = key === endKey;
            timed[key].push({
                event,
                startMin: isFirst ? startMin : 0,
                endMin: isLast ? Math.max(endMinOnLastDay, isFirst ? startMin : 0) : MINUTES_PER_DAY,
                continued: !isFirst,
                continues: !isLast,
            });
        }
    }
    return { band, timed };
}

/**
 * Side-by-side layout for one day column.
 *
 * Sort by (start asc, end desc), sweep into clusters of transitively
 * overlapping segments, give each segment the first lane whose previous
 * occupant has ended, then let it widen into free lanes to its right.
 * Overlap is decided on REAL minutes, not rendered height, so two blocks that
 * merely look close are not squeezed.
 *
 * @returns {Array<{ segment, top, height, lane, lanes, span }>}
 */
export function layoutDay(segments, { hourPx = HOUR_PX } = {}) {
    const sorted = [...(segments || [])].sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
    const placed = [];
    let cluster = [];
    let clusterEnd = -1;

    const flush = () => {
        if (!cluster.length) return;
        const laneEnds = [];
        for (const item of cluster) {
            let lane = laneEnds.findIndex((end) => end <= item.segment.startMin);
            if (lane === -1) {
                lane = laneEnds.length;
                laneEnds.push(0);
            }
            laneEnds[lane] = Math.max(item.segment.endMin, item.segment.startMin + 1);
            item.lane = lane;
        }
        const lanes = laneEnds.length;
        for (const item of cluster) {
            // Widen into lanes to the right that nothing overlapping occupies.
            let span = 1;
            for (let next = item.lane + 1; next < lanes; next += 1) {
                const blocked = cluster.some((o) => o !== item && o.lane === next
                    && o.segment.startMin < item.segment.endMin && item.segment.startMin < o.segment.endMin);
                if (blocked) break;
                span += 1;
            }
            item.lanes = lanes;
            item.span = span;
            placed.push(item);
        }
        cluster = [];
        clusterEnd = -1;
    };

    for (const segment of sorted) {
        if (cluster.length && segment.startMin >= clusterEnd) flush();
        const durationPx = ((segment.endMin - segment.startMin) / 60) * hourPx;
        cluster.push({
            segment,
            top: (segment.startMin / 60) * hourPx + 2,
            height: Math.max(MIN_BLOCK_PX, durationPx - 4),
        });
        clusterEnd = Math.max(clusterEnd, segment.endMin, segment.startMin + 1);
    }
    flush();
    return placed;
}

/**
 * Rows for the all-day band: each event spans the columns it covers in view,
 * and takes the first row free across that whole span.
 *
 * @param {Array<{event, range}>} bandEntries - from splitAllDay
 * @param {string[]} dayKeys
 * @returns {Array<{ event, startCol, endCol, row, clippedStart, clippedEnd }>} endCol inclusive
 */
export function layoutBand(bandEntries, dayKeys) {
    const first = dayKeys[0];
    const cols = dayKeys.length;
    const entries = (bandEntries || []).map(({ event, range }) => {
        const startCol = Math.max(0, daysBetween(first, range[0]));
        const endCol = Math.min(cols - 1, daysBetween(first, range[1]) - 1);
        return {
            event, startCol, endCol,
            clippedStart: daysBetween(first, range[0]) < 0,
            clippedEnd: daysBetween(first, range[1]) - 1 > cols - 1,
        };
    }).filter((e) => e.endCol >= e.startCol)
        .sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));

    const rowEnds = [];
    for (const entry of entries) {
        let row = rowEnds.findIndex((end) => end < entry.startCol);
        if (row === -1) {
            row = rowEnds.length;
            rowEnds.push(-1);
        }
        rowEnds[row] = entry.endCol;
        entry.row = row;
    }
    return entries;
}

/** Events bucketed by the zone day they START on, for Month cells. */
export function bucketByDay(events, dayKeys, zone) {
    const buckets = Object.fromEntries(dayKeys.map((k) => [k, []]));
    for (const event of events || []) {
        if (isAllDayLike(event)) {
            const range = allDayRange(event, zone);
            for (const key of dayKeys) if (coversDay(range, key)) buckets[key].push(event);
            continue;
        }
        const key = dayKeyOf(event.start, zone);
        if (buckets[key]) buckets[key].push(event);
    }
    // All-day first, then by start — the order a busy cell reads best in.
    for (const key of dayKeys) {
        buckets[key].sort((a, b) => Number(isAllDayLike(b)) - Number(isAllDayLike(a))
            || (parseInstant(a.start) || 0) - (parseInstant(b.start) || 0));
    }
    return buckets;
}
